<template>
  <v-container class="d-flex align-center flex-wrap pa-2 ga-2" elevation="1">
    <HeadingToolbarButtons :editor="editor" :isDisabled="props.isDisabled" />

    <!-- Inline Format Buttons -->
    <v-btn-toggle multiple density="compact" :disabled="props.isDisabled">
      <v-btn @click="exec('toggleBold')" :class="active('bold')">
        <v-icon>mdi-format-bold</v-icon>
      </v-btn>
      <v-btn @click="exec('toggleItalic')" :class="active('italic')">
        <v-icon>mdi-format-italic</v-icon>
      </v-btn>

      <v-btn @click="exec('toggleUnderline')" :class="active('underline')">
        <v-icon>mdi-format-underline</v-icon>
      </v-btn>

      <v-btn @click="exec('toggleBulletList')" :class="active('bulletList')">
        <v-icon>mdi-format-list-bulleted</v-icon>
      </v-btn>
      <v-btn @click="exec('toggleOrderedList')" :class="active('orderedList')">
        <v-icon>mdi-format-list-numbered</v-icon>
      </v-btn>
    </v-btn-toggle>

    <v-divider vertical class="mx-1" />

    <!-- Undo/Redo -->
    <v-btn-toggle mandatory density="compact" :disabled="props.isDisabled">
      <v-btn @click="exec('undo')">
        <v-icon>mdi-undo</v-icon>
      </v-btn>
      <v-btn @click="exec('redo')">
        <v-icon>mdi-redo</v-icon>
      </v-btn>
    </v-btn-toggle>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { Editor } from '@tiptap/vue-3'
import HeadingToolbarButtons from './HeadingToolbarButtons.vue'

const props = defineProps<{
  title?: string
  editor: Editor
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  isDisabled: boolean
  vehicle: any
}>()

const alignments = ['left', 'center', 'right', 'justify'] as const

const alignmentIcons: Record<string, string> = {
  left: 'mdi-format-align-left',
  center: 'mdi-format-align-center',
  right: 'mdi-format-align-right',
  justify: 'mdi-format-align-justify',
}

const panel = ref<number[]>(props.isOpen ? [0] : [])

watch(panel, (newVal) => {
  if (newVal.length > 0) {
    props.setIsOpen(true)
  } else {
    props.setIsOpen(false)
  }
})

const toggleOpen = () => {
  props.setIsOpen(!props.isOpen)
}

const setAlign = (align: string) => {
  props.editor.chain().focus().setTextAlign(align).run()
}

const isTextAlignActive = (align: string) => {
  return props.editor.isActive({ textAlign: align })
}

const active = (type: string) => {
  return props.editor.isActive(type) ? 'is-active' : ''
}

const exec = (command: string) => {
  const chain = props.editor.chain().focus()
  // Type assertion to tell TypeScript this is a method that can be called
  const method = chain[command as keyof typeof chain] as (...args: any[]) => typeof chain
  method().run()
}

const activeClass = (type: string) => {
  return props.editor.isActive(type) ? 'bg-grey-lighten-3' : ''
}

const setLink = () => {
  const previousUrl = props.editor.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)

  if (url === null) return
  if (url === '') {
    props.editor.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }

  props.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}
</script>

<style scoped>
.v-btn.is-active {
  background-color: #666;
  color: white;
}
</style>
